import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Chip, FormControlLabel, makeStyles, MenuItem, Paper, TextField, Typography } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../../../auth';
import {
  blogErrorMessage, getBlogCapabilities, getManagedBlogPost,
  createBlogTag, listBlogTags, listManagedBlogPosts, saveBlogPost, setBlogPublicationBlock,
} from '../../../Apis/touzenBlogApi';
import { BlogDate, BlogLayout, BlogPagination } from './BlogPublicPage';
import { BlogAuthorName, OwnBlogShare, PublicNameSettingsLink, usePublicNameProfile } from '../components/PublicNameSettings';

const useStyles = makeStyles((theme) => ({
  actions: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2), margin: theme.spacing(3, 0), '& a, & button': { minHeight: 44 } },
  saveActions: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 180px)', gridAutoRows: '1fr', justifyContent: 'end', gap: '16px', margin: theme.spacing(3, 0),
    '@media screen and (max-width:750px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  editorNavigation: {
    display: 'grid', gridTemplateColumns: 'repeat(2, 180px)', gridAutoRows: '1fr', justifyContent: 'start', gap: '16px',
    borderTop: `1px solid ${theme.palette.divider}`, marginTop: theme.spacing(4), paddingTop: theme.spacing(3), marginBottom: theme.spacing(3),
    '@media screen and (max-width:750px)': { gridTemplateColumns: 'minmax(0, 1fr)' },
  },
  card: { padding: theme.spacing(3), marginTop: theme.spacing(3), overflowWrap: 'anywhere', scrollMarginTop: '128px' },
  list: { listStyle: 'none', padding: 0, margin: 0 },
  row: {
    padding: theme.spacing(3, 0), borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: theme.spacing(3),
    [theme.breakpoints.down('xs')]: { alignItems: 'flex-start', flexDirection: 'column', gap: 0 },
  },
  rowText: { minWidth: 0, overflowWrap: 'anywhere', '& $states': { margin: theme.spacing(0, 0, 0.5), '& .MuiChip-root': { height: 20 } } },
  dates: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1, 3), fontSize: '0.875rem', color: theme.palette.text.secondary },
  reactionInfo: { color: '#666666' },
  states: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), margin: theme.spacing(2, 0) },
  publishedState: { backgroundColor: '#426654', color: '#FFF', borderColor: '#426654' },
  draftState: { backgroundColor: '#E7E2DB', color: '#333', borderColor: '#E7E2DB' },
  blockedState: { backgroundColor: '#7C2A2E', color: '#FFF', borderColor: '#7C2A2E' },
  filters: {
    display: 'grid', gap: '16px', padding: theme.spacing(2, 0), marginBottom: theme.spacing(3), borderTop: `1px solid ${theme.palette.divider}`, borderBottom: `1px solid ${theme.palette.divider}`,
    '& .MuiInputBase-root': { fontFamily: theme.typography.fontFamily, fontSize: 16, borderRadius: 8, minHeight: 44, boxShadow: 'inset 0 0 15px rgba(0,0,0,0.1)' },
    '& .MuiInputLabel-root': { fontSize: 14 }, '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(1)' },
    '& .MuiFormHelperText-root': { fontSize: 14 }, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#999', borderWidth: '0.5px', '& legend': { fontSize: 14 } },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
  },
  filterRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' },
  searchRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '16px' },
  searchField: { flex: '1 1 260px', minWidth: 0 },
  selectField: { width: 240, maxWidth: '100%' },
  filterMenu: { border: '0.5px solid #999', borderRadius: 8 },
  filterOption: { fontFamily: theme.typography.fontFamily, fontSize: 16, minHeight: 44, whiteSpace: 'normal', '&.Mui-selected': { color: theme.palette.primary.main, backgroundColor: alpha(theme.palette.primary.main, 0.08) } },
  articleTags: { display: 'flex', flexWrap: 'wrap', gap: '4px 8px', margin: 0 },
  articleTag: {
    display: 'inline-flex', alignItems: 'center', minHeight: 44, maxWidth: '100%', padding: '0 4px', fontSize: '0.875rem', fontStyle: 'italic',
    color: '#666666', textDecoration: 'none', overflowWrap: 'anywhere',
    '&:visited': { color: '#666666' }, '&:hover, &:focus': { color: theme.palette.primary.main },
    '&[aria-current="true"]': { color: theme.palette.primary.main, textDecoration: 'underline' },
    '&:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  },
  form: { '& > div': { marginBottom: theme.spacing(3) } },
  field: { display: 'grid', gap: theme.spacing(1) },
  tags: { border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius, padding: theme.spacing(2), margin: theme.spacing(3, 0) },
  choices: { display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1), overflowWrap: 'anywhere' },
  image: { display: 'block', maxWidth: '100%', maxHeight: 280, objectFit: 'contain' },
  details: { margin: theme.spacing(3, 0), '& summary': { cursor: 'pointer', padding: theme.spacing(1, 0), minHeight: 44 }, '& > div': { marginTop: theme.spacing(2) } },
  alert: { margin: theme.spacing(2, 0), color: theme.palette.error.main, overflowWrap: 'anywhere' },
  surface: {
    fontSize: '1rem', lineHeight: 1.7,
    '& .MuiTypography-body1': { fontSize: '1rem', lineHeight: 1.7 },
    '& .MuiTypography-body2': { fontSize: '0.875rem', lineHeight: 1.7 },
    '& .MuiTypography-h5': { fontSize: '1.25rem', lineHeight: 1.5 },
    '& .MuiChip-root': { fontSize: '0.75rem', height: 24 },
    '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
    '& .MuiButton-root.MuiButtonBase-root, & input[type="file"]::file-selector-button': {
      fontFamily: theme.typography.fontFamily, fontSize: '0.875rem', fontWeight: 'bold', lineHeight: 'normal',
      minHeight: 'auto', borderRadius: '0.9em', border: 'none', padding: '8px 12px',
      backgroundColor: 'rgba(192,192,192, 1)', boxShadow: '0px 0px 15px rgba(0,0,0,0.1)', color: '#000', textTransform: 'none', cursor: 'pointer',
    },
    '& .MuiButton-root.MuiButtonBase-root:hover, & input[type="file"]::file-selector-button:hover': { backgroundColor: 'rgba(192,192,192, 1)', boxShadow: '0px 0px 15px rgba(0,0,0,0.1)' },
    '& .MuiButton-root:focus-visible, & .MuiButton-root.Mui-focusVisible, & input[type="file"]:focus-visible': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
    '& .MuiButton-root.Mui-disabled, & input[type="file"]:disabled::file-selector-button': { opacity: 0.5, cursor: 'default' },
  },
  editor: {
    '& h1.MuiTypography-root': { fontSize: '1.25rem', lineHeight: 1.5, marginBottom: theme.spacing(3) },
    '& h2.MuiTypography-root': { fontSize: '1rem', fontWeight: 'bold', lineHeight: 1.5 },
    '& .MuiInputBase-root': { fontFamily: theme.typography.fontFamily, fontSize: 16, borderRadius: 8, boxShadow: 'inset 0 0 15px rgba(0,0,0,0.1)' },
    '& .MuiInputLabel-root, & label': { fontSize: 14, fontFamily: theme.typography.fontFamily },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(1)' },
    '& .MuiFormHelperText-root': { fontSize: '0.875rem', lineHeight: 1.7 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#999', borderWidth: '0.5px' },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.primary.main },
    '& .MuiFormControlLabel-label': { fontSize: '1rem', lineHeight: 1.5 },
    '& $tags > legend': { fontSize: '1rem', fontWeight: 'bold' },
    '& summary': { fontSize: '1rem', lineHeight: 1.7 },
    '& input[type="file"]': { fontFamily: theme.typography.fontFamily, fontSize: '0.875rem', width: '100%', maxWidth: '100%', padding: '8px 0' },
  },
  touchAction: { '&&.MuiButton-root.MuiButtonBase-root': { minHeight: 44 } },
  indexHeading: { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)', whiteSpace: 'nowrap', border: 0 },
  indexShare: {
    '& h2.MuiTypography-root': { fontSize: '1rem', fontWeight: 'bold', lineHeight: 1.5 },
    '& p.MuiTypography-root': { fontSize: '0.875rem', lineHeight: 1.7 },
  },
}));

const blankForm = { title: '', slug: '', excerpt: '', body: '' };
const saveNotices = {
  draft_saved: '下書きを保存しました。',
  published: '記事を公開しました。',
  publication_blocked: '公開設定を保存しました。管理者による差し止め中のため公開されません。',
};
const withoutNotice = (state) => {
  if (!state || !Object.prototype.hasOwnProperty.call(state, 'blogNotice')) return state;
  const next = { ...state };
  delete next.blogNotice;
  return Object.keys(next).length ? next : null;
};
const labels = { title: 'タイトル', slug: '記事URLの末尾（Slug）', excerpt: '概要', body: '本文', cover_image: '代表画像', tag_ids: 'タグ' };
const fieldIds = Object.fromEntries(Object.keys(labels).map((field) => [field, `blog-${field}`]));
const reactionCount = (value) => Number.isSafeInteger(value) && value >= 0 ? value : '—';
const statusOptions = { '': 'すべての状態', draft: '下書き', published: '公開に設定した記事', blocked: '公開差し止め' };
const sortOptions = { updated_desc: '更新が新しい順', updated_asc: '更新が古い順', published_desc: '公開日が新しい順', title_asc: 'タイトル順', likes_desc: 'いいねが多い順', shares_desc: '共有操作数が多い順', author_asc: '投稿者名順', author_desc: '投稿者名逆順' };
const managedPage = (raw) => /^[1-9]\d*$/.test(raw || '') && Number.isSafeInteger(Number(raw)) ? Number(raw) : 1;
const coverTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];
const isPdfCover = (file) => Boolean(file && (file.type === 'application/pdf'
  || (['', 'application/octet-stream'].includes(file.type) && typeof file.name === 'string' && file.name.toLowerCase().endsWith('.pdf'))));
function validateImage(file) {
  if (!file) return '';
  if (file.size > 5 * 1024 * 1024) return '代表画像は5MiB以下で選択してください。';
  if (!coverTypes.includes(file.type) && !isPdfCover(file)) return '代表画像はJPEG・PNG・WebP・GIF・PDFから選択してください。';
  return '';
}
function formFor(post) {
  return Object.fromEntries(Object.keys(blankForm).map((field) => [field, post?.[field] || '']));
}
function validate(form) {
  const errors = {};
  ['title', 'body'].forEach((field) => {
    if (!form[field].trim()) errors[field] = `${labels[field]}を入力してください。`;
  });
  ['title', 'slug'].forEach((field) => {
    if (form[field].length > 255) errors[field] = '255文字以内で入力してください。';
  });
  if (form.excerpt.length > 180) errors.excerpt = '180文字以内で入力してください。';
  if (form.slug && !/^[-a-zA-Z0-9_]+$/.test(form.slug)) errors.slug = '半角英数字・ハイフン・アンダースコアで入力してください。';
  return errors;
}

function PostStates({ post }) {
  const classes = useStyles();
  return <div className={classes.states}>
    {(!post.is_publication_blocked || post.status === 'draft') && <Chip label={post.status === 'published' ? '公開' : '下書き'} className={post.status === 'published' ? classes.publishedState : classes.draftState} variant="outlined" />}
    {post.is_publication_blocked && <Chip label="公開差し止め" className={classes.blockedState} variant="outlined" />}
  </div>;
}

function BlogTagEditor({ ids, onChange, disabled, sendingRef, onBusyChange }) {
  const classes = useStyles();
  const [catalog, setCatalog] = useState({ loading: true, items: [], error: null });
  const [attempt, setAttempt] = useState(0);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const active = useRef(false);
  const created = useRef(new Map());
  const controller = useRef(null);
  useEffect(() => {
    active.current = true;
    controller.current = new AbortController();
    return () => { active.current = false; controller.current.abort(); };
  }, []);
  useEffect(() => {
    const requestController = new AbortController();
    const earlierCreations = new Set(created.current.keys());
    setCatalog((current) => ({ ...current, loading: true, error: null }));
    listBlogTags(requestController.signal).then((items) => {
      if (!active.current || requestController.signal.aborted) return;
      earlierCreations.forEach((id) => created.current.delete(id));
      const merged = new Map([...created.current, ...items.map((tag) => [tag.id, tag])]);
      setCatalog({ loading: false, items: [...merged.values()], error: null });
    }).catch((failure) => {
      if (active.current && !requestController.signal.aborted) setCatalog((current) => ({ ...current, loading: false, error: failure }));
    });
    return () => requestController.abort();
  }, [attempt]);

  const create = async () => {
    if (disabled || sendingRef.current) return;
    setMessage(''); setError('');
    if (!name.trim()) { setError('タグ名を入力してください。'); return; }
    if (ids.length >= 100) { setError('この記事のタグは100件まで選択できます。'); return; }
    sendingRef.current = true;
    setCreating(true); onBusyChange(true);
    try {
      const tag = await createBlogTag(name, controller.current.signal);
      if (!active.current || controller.current.signal.aborted) return;
      created.current.set(tag.id, tag);
      setCatalog((current) => ({ ...current, items: [...new Map([...current.items.map((item) => [item.id, item]), [tag.id, tag]]).values()] }));
      onChange([...new Set([...ids, tag.id])]);
      setName('');
      setMessage('タグを選択しました。記事の保存で反映されます。');
    } catch (failure) {
      if (active.current && !controller.current.signal.aborted) setError(['timeout', 'network', 'unknown', 'session_changed', 'invalid_response'].includes(failure?.kind)
        ? 'タグの作成結果を確認できていません。候補を再読み込みして確認してから操作してください。'
        : `タグを作成できませんでした。${blogErrorMessage(failure)}`);
    } finally {
      sendingRef.current = false;
      if (active.current) { setCreating(false); onBusyChange(false); }
    }
  };

  return <fieldset id={fieldIds.tag_ids} tabIndex={-1} className={classes.tags} disabled={disabled || creating}>
    <legend>タグ（任意・100件まで）</legend>
    <Typography variant="body2">タグ候補は全投稿者で共有します。新規タグは作成時に候補へ追加され、記事との紐付けは記事の保存時に反映されます。</Typography>
    {catalog.loading && <Typography role="status">タグ候補を読み込んでいます。</Typography>}
    {catalog.error && <Typography role="alert">タグ候補を取得できませんでした。本文と選択は保持しています。{blogErrorMessage(catalog.error)}</Typography>}
    <div className={classes.choices}>{catalog.items.map((tag) => <FormControlLabel key={tag.id} label={tag.name}
      control={<Checkbox checked={ids.includes(tag.id)} disabled={disabled || creating || (!ids.includes(tag.id) && ids.length >= 100)}
        onChange={(event) => { onChange(event.target.checked ? [...ids, tag.id] : ids.filter((id) => id !== tag.id)); setMessage(''); }} />} />)}</div>
    {!catalog.loading && !catalog.error && catalog.items.length === 0 && <Typography variant="body2">タグ候補はまだありません。</Typography>}
    <Button type="button" onClick={() => setAttempt((value) => value + 1)} disabled={disabled || creating || catalog.loading}>タグ候補を再読み込み</Button>
    <TextField id="blog-new-tag" label="新しいタグ名" value={name} fullWidth variant="outlined" disabled={disabled || creating}
      helperText="50文字以内。作成すると全投稿者の候補に追加されます。" onChange={(event) => setName(event.target.value)}
      onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }} />
    <Button type="button" onClick={create} disabled={disabled || creating}>タグを作成して選択</Button>
    {creating && <Typography role="status">タグを作成しています。</Typography>}
    {message && <Typography role="status">{message}</Typography>}
    {error && <Typography role="alert">{error}</Typography>}
  </fieldset>;
}

function ManageContent({ isAuth, postId, creating }) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const statusFilter = searchParams.get('status') ?? '';
  const sort = searchParams.get('sort') ?? 'updated_desc';
  const tagRaw = searchParams.get('tag');
  const tag = tagRaw === null ? undefined : /^[1-9]\d*$/.test(tagRaw) && Number.isSafeInteger(Number(tagRaw)) ? Number(tagRaw) : NaN;
  const page = searchParams.getAll('page').length > 1 ? 1 : managedPage(searchParams.get('page'));
  const invalidQuery = ['q', 'status', 'sort', 'tag'].some((field) => searchParams.getAll(field).length > 1);
  const [qDraft, setQDraft] = useState(q);
  const [notice, setNotice] = useState('');
  const [tagCatalog, setTagCatalog] = useState({ loading: false, items: [], error: null });
  const [tagCatalogAttempt, setTagCatalogAttempt] = useState(0);
  const publicName = usePublicNameProfile({ refreshOnFocus: true });
  const [state, setState] = useState({ loading: true, capabilities: null, posts: [], post: null, pagination: null, error: null, requestKey: '' });
  const [form, setForm] = useState(blankForm);
  const [errors, setErrors] = useState({});
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [coverFile, setCoverFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [otherSettingsOpen, setOtherSettingsOpen] = useState(false);
  const [tagIds, setTagIds] = useState([]);
  const [tagsChanged, setTagsChanged] = useState(false);
  const [tagBusy, setTagBusy] = useState(false);
  const tagSending = useRef(false);
  const fileInputRef = useRef(null);
  const mounted = useRef(false);
  const sending = useRef(false);
  const controllerRef = useRef(null);
  const summaryRef = useRef(null);
  const publicationRef = useRef(null);
  const editing = creating || Boolean(postId);
  const listing = useMemo(() => editing ? null : { options: { q, status: statusFilter, sort, page, ...(tag === undefined ? {} : { tag }) }, invalidQuery }, [editing, q, statusFilter, sort, page, tag, invalidQuery]);
  const requestKey = editing ? 'editor' : JSON.stringify(listing);
  const loading = state.loading || (!editing && state.requestKey !== requestKey);
  const query = searchParams.toString() ? `?${searchParams}` : '';
  const withQuery = (path) => `${path}${query}`;
  const tagHref = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tag', String(id)); next.delete('page');
    return `/blog/manage?${next}`;
  };
  const ownPost = !state.post || state.post.is_own_post;
  const authorName = ownPost ? publicName.profile?.display_name || '' : state.post.author_display_name;
  const pdfSelected = isPdfCover(coverFile);

  useEffect(() => { setQDraft(q); }, [q]);
  useEffect(() => {
    if (!isAuth || editing || !state.capabilities?.can_manage) return undefined;
    let active = true;
    const controller = new AbortController();
    setTagCatalog((current) => ({ ...current, loading: true, error: null }));
    listBlogTags(controller.signal).then((items) => { if (active) setTagCatalog({ loading: false, items, error: null }); })
      .catch((error) => { if (active) setTagCatalog((current) => ({ ...current, loading: false, error })); });
    return () => { active = false; controller.abort(); };
  }, [isAuth, editing, state.capabilities?.can_manage, tagCatalogAttempt]);
  useEffect(() => {
    if (editing || !location.state || !Object.prototype.hasOwnProperty.call(location.state, 'blogNotice')) return;
    setNotice(Object.prototype.hasOwnProperty.call(saveNotices, location.state.blogNotice) ? saveNotices[location.state.blogNotice] : '');
    navigate({ pathname: location.pathname, search: location.search, hash: location.hash }, { replace: true, state: withoutNotice(location.state) });
  }, [editing, location, navigate]);
  useEffect(() => {
    if (editing) return;
    const normalized = state.requestKey === requestKey && state.pagination && !state.error ? state.pagination.page : page;
    const next = new URLSearchParams(searchParams);
    if (normalized === 1) next.delete('page'); else next.set('page', String(normalized));
    if (next.toString() !== searchParams.toString()) navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { replace: true, state: withoutNotice(location.state) });
  }, [editing, page, searchParams, navigate, location, state.requestKey, state.pagination, state.error, requestKey]);
  useEffect(() => {
    if (location.hash === '#publication-control' && !loading && state.post?.id && state.capabilities?.can_moderate) {
      publicationRef.current?.scrollIntoView?.({ block: 'start' });
      publicationRef.current?.focus();
    }
  }, [location.hash, loading, state.post?.id, state.capabilities?.can_moderate]);

  useEffect(() => {
    if (!coverFile || validateImage(coverFile) || isPdfCover(coverFile)) { setPreviewUrl(''); return; }
    const url = URL.createObjectURL(coverFile);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [coverFile]);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    controllerRef.current = controller;
    if (!isAuth) {
      setState({ loading: false, capabilities: null, posts: [], post: null, pagination: null, error: null, requestKey });
      return () => { mounted.current = false; controller.abort(); };
    }
    setState((current) => ({ loading: true, capabilities: editing ? null : current.capabilities, posts: [], post: null, pagination: null, error: null, requestKey }));
    getBlogCapabilities(controller.signal).then(async (capabilities) => {
      if (!mounted.current || controller.signal.aborted) return;
      if (!capabilities.can_manage) {
        setState({ loading: false, capabilities, posts: [], post: null, pagination: null, error: null, requestKey });
        return;
      }
      setState((current) => ({ ...current, capabilities }));
      if (listing?.invalidQuery) throw Object.assign(new Error('TOUZEN_BLOG_QUERY_INVALID'), { kind: 'validation' });
      const data = creating ? null : postId
        ? await getManagedBlogPost(postId, controller.signal)
        : await listManagedBlogPosts(controller.signal, listing.options);
      if (!mounted.current || controller.signal.aborted) return;
      setState({ loading: false, capabilities, posts: editing ? [] : data.results, pagination: editing ? null : data, post: editing ? data : null, error: null, requestKey });
      if (editing) { setForm(formFor(data)); setTagIds(data?.tag_ids || []); setTagsChanged(false); }
    }).catch((error) => {
      if (mounted.current && !controller.signal.aborted) setState((current) => ({ ...current, loading: false, posts: [], post: null, pagination: null, error, requestKey }));
    });
    return () => { mounted.current = false; controller.abort(); };
  }, [isAuth, creating, postId, editing, attempt, requestKey, listing]);

  const focusSummary = () => { setTimeout(() => { if (mounted.current) summaryRef.current?.focus(); }, 0); };
  const save = async (status) => {
    if (sending.current || tagSending.current) return;
    setSuccess('');
    setActionError(null);
    const nextErrors = validate(form);
    const imageError = validateImage(coverFile);
    if (imageError) nextErrors.cover_image = imageError;
    if (status === 'published' && !authorName.trim()) nextErrors.public_name = ownPost && (publicName.loading || publicName.error)
      ? '公開名を確認してから公開してください。' : '公開する前に記事所有者の共通公開名を設定してください。';
    setErrors(nextErrors);
    if (nextErrors.slug || nextErrors.excerpt) setOtherSettingsOpen(true);
    if (Object.keys(nextErrors).length) { focusSummary(); return; }
    sending.current = true;
    setBusy(true);
    try {
      const post = await saveBlogPost(state.post?.id || postId, { ...form, status, ...(coverFile ? { cover_image: coverFile } : {}), ...(tagsChanged ? { tag_ids: tagIds } : {}) }, controllerRef.current.signal);
      if (!mounted.current || controllerRef.current.signal.aborted) return;
      setState((current) => ({ ...current, post }));
      setForm(formFor(post));
      setTagIds(post.tag_ids); setTagsChanged(false);
      setCoverFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      const code = status === 'draft' ? 'draft_saved' : post.is_publication_blocked ? 'publication_blocked' : 'published';
      navigate(`/blog/manage${query}`, { replace: true, state: { ...location.state, blogNotice: code } });
    } catch (error) {
      if (mounted.current && !controllerRef.current.signal.aborted) {
        setActionError(error);
        setErrors(Object.fromEntries((error.fields || []).filter((field) => labels[field]).map((field) => [field, `${labels[field]}を確認してください。`])));
        if (error.fields?.some((field) => ['slug', 'excerpt'].includes(field))) setOtherSettingsOpen(true);
        focusSummary();
      }
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const moderate = async () => {
    if (sending.current || tagSending.current || !state.capabilities?.can_moderate || !state.post) return;
    sending.current = true;
    setBusy(true);
    setSuccess('');
    setActionError(null);
    try {
      const post = await setBlogPublicationBlock(state.post.id, !state.post.is_publication_blocked, controllerRef.current.signal);
      if (!mounted.current || controllerRef.current.signal.aborted) return;
      // Keep unsaved editor input; this operation changes only the independent block.
      setState((current) => ({ ...current, post }));
      setSuccess(post.is_publication_blocked ? '公開を差し止めました。' : '公開差し止めを解除しました。');
    } catch (error) {
      if (mounted.current && !controllerRef.current.signal.aborted) { setActionError(error); focusSummary(); }
    } finally {
      sending.current = false;
      if (mounted.current) setBusy(false);
    }
  };

  const textField = (field) => <TextField key={field} id={fieldIds[field]} label={labels[field]}
    fullWidth variant="outlined" required={['title', 'body'].includes(field)}
    placeholder={field === 'title' ? '記事のタイトルを入力してください' : undefined}
    InputLabelProps={field === 'title' ? { shrink: true } : undefined}
    multiline={['excerpt', 'body'].includes(field)} minRows={field === 'body' ? 12 : field === 'excerpt' ? 3 : undefined}
    autoComplete="off" value={form[field]} disabled={busy} error={Boolean(errors[field])}
    helperText={errors[field] || (field === 'slug' ? '記事URL「/blog/posts/my-article」の末尾「my-article」を設定します。空欄なら自動設定します。半角英数字と「-」「_」が使えます。' : field === 'body' ? '本文はテキストとして表示されます。' : field === 'excerpt' ? '一覧・関連記事のサムネイル表示に使用する短い紹介文です（任意・180文字以内）。空欄の場合は、本文の冒頭から抜粋して表示します。' : undefined)}
    onChange={(event) => { const value = event.target.value; setForm((current) => ({ ...current, [field]: value })); setSuccess(''); }} />;

  const changeFilters = (changes) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([field, value]) => { if (value === '') next.delete(field); else next.set(field, value); });
    next.delete('page'); setNotice('');
    if (next.toString() !== searchParams.toString()) navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { state: withoutNotice(location.state) });
  };
  const changePage = (nextPage) => {
    if (nextPage === (state.pagination?.page || page)) return;
    const next = new URLSearchParams(searchParams);
    if (nextPage === 1) next.delete('page'); else next.set('page', String(nextPage));
    setNotice(''); navigate({ pathname: location.pathname, search: next.toString() ? `?${next}` : '', hash: location.hash }, { state: withoutNotice(location.state) });
  };
  const selectProps = { displayEmpty: true, MenuProps: { classes: { paper: classes.filterMenu } } };
  const qTooLong = Array.from(qDraft.trim()).length > 200;

  return <div className={`${classes.surface}${editing ? ` ${classes.editor}` : ''}`}>
    {!editing && <h1 className={classes.indexHeading}>ブログ管理</h1>}
    {!editing && isAuth && state.capabilities?.can_manage && <section className={classes.filters} aria-label="管理記事の絞り込み">
      <form className={classes.searchRow} onSubmit={(event) => { event.preventDefault(); if (!qTooLong) { setQDraft(qDraft.trim()); changeFilters({ q: qDraft.trim() }); } }}>
        <TextField id="blog-manage-q" className={classes.searchField} label="記事を検索" variant="outlined" size="small" value={qDraft} InputLabelProps={{ shrink: true }}
          error={qTooLong} helperText={qTooLong ? '200文字以内で入力してください。' : 'タイトル・投稿者名・URL末尾・概要・本文を検索します。'} onChange={(event) => setQDraft(event.target.value)} />
        <Button className={classes.touchAction} type="submit" disabled={qTooLong}>検索</Button>
        <Button className={classes.touchAction} type="button" onClick={() => { setQDraft(''); changeFilters({ q: '', status: '', sort: '', tag: '' }); }}>条件をクリア</Button>
      </form>
      <div className={classes.filterRow}>
        <TextField id="blog-manage-status" className={classes.selectField} label="状態" select variant="outlined" size="small" value={statusFilter} InputLabelProps={{ shrink: true }} SelectProps={selectProps}
          onChange={(event) => changeFilters({ status: event.target.value })}>
          {!Object.prototype.hasOwnProperty.call(statusOptions, statusFilter) && <MenuItem value={statusFilter} className={classes.filterOption}>無効な状態</MenuItem>}
          {Object.entries(statusOptions).map(([value, label]) => <MenuItem key={value} value={value} className={classes.filterOption}>{label}</MenuItem>)}
        </TextField>
        <TextField id="blog-manage-sort" className={classes.selectField} label="並べ替え" select variant="outlined" size="small" value={sort} InputLabelProps={{ shrink: true }} SelectProps={selectProps}
          onChange={(event) => changeFilters({ sort: event.target.value })}>
          {!Object.prototype.hasOwnProperty.call(sortOptions, sort) && <MenuItem value={sort} className={classes.filterOption}>無効な並び順</MenuItem>}
          {Object.entries(sortOptions).map(([value, label]) => <MenuItem key={value} value={value} className={classes.filterOption}>{label}</MenuItem>)}
        </TextField>
        <TextField id="blog-manage-tag" className={classes.selectField} label="タグ" select variant="outlined" size="small" value={tagRaw ?? ''} InputLabelProps={{ shrink: true }} SelectProps={selectProps}
          onChange={(event) => changeFilters({ tag: event.target.value })}>
          <MenuItem value="" className={classes.filterOption}>すべてのタグ</MenuItem>
          {tagRaw !== null && !tagCatalog.items.some((item) => String(item.id) === tagRaw) && <MenuItem value={tagRaw} className={classes.filterOption}>選択中のタグ（候補未取得）</MenuItem>}
          {tagCatalog.items.map((item) => <MenuItem key={item.id} value={String(item.id)} className={classes.filterOption}>{item.name}</MenuItem>)}
        </TextField>
      </div>
      {tagCatalog.loading && <Typography variant="body2" role="status">タグ候補を読み込んでいます。</Typography>}
      {tagCatalog.error && <div role="alert"><Typography variant="body2">タグ候補を読み込めませんでした。記事の表示は保持しています。</Typography><Button className={classes.touchAction} onClick={() => setTagCatalogAttempt((value) => value + 1)}>タグ候補を再読み込み</Button></div>}
    </section>}
    {!isAuth ? <>
      {editing && <Typography component="h1" variant="h4">ブログ管理</Typography>}
      <Typography>刀禅アカウントでログインしてください。</Typography>
      <div className={classes.actions}><Button component={Link} role="link" to="/login" state={{ previous: '/blog/manage' }} variant="contained" color="primary">ログイン</Button></div>
    </> : loading ? <Typography role="status">投稿資格と記事を確認しています。</Typography>
      : state.error ? <div role="alert"><Typography>{blogErrorMessage(state.error)}</Typography><Button onClick={() => setAttempt((value) => value + 1)}>再読み込み</Button></div>
        : !state.capabilities?.can_manage ? <Typography role="alert">ブログを管理する資格がありません。主宰者・稼働中の講師・サイト管理者が利用できます。</Typography>
          : <>
            {editing && <Typography component="h1" variant="h4">{state.post ? '記事を編集' : '記事を新規作成'}</Typography>}
            {!editing && <>
              {notice && <Typography role="status">{notice}</Typography>}
              <div className={classes.actions}><Button className={classes.touchAction} component={Link} role="link" to={withQuery('/blog/manage/new')} variant="contained" color="primary">記事を新規作成</Button></div>
              {state.posts.length === 0 && <Typography>{q.trim() || statusFilter || tagRaw !== null ? '条件に一致する記事はありません。' : '管理できる記事はありません。'}</Typography>}
              <ul className={classes.list} aria-label="管理記事">{state.posts.map((post) => <li key={post.id}><article className={classes.row}>
                <div className={classes.rowText}>
                  <PostStates post={post} />
                  <Typography component="h2" variant="h5">{post.title}</Typography>
                  <Typography variant="body2" className={classes.reactionInfo}>投稿者：{post.author_display_name?.trim() || '未設定'}</Typography>
                  {Array.isArray(post.tags) && post.tags.length > 0 && <div role="group" aria-label="記事のタグ" className={classes.articleTags}>{post.tags.map((item) => <Link key={item.id} className={classes.articleTag}
                    to={tagHref(item.id)} state={withoutNotice(location.state)} aria-current={tag === item.id ? 'true' : undefined} onClick={() => setNotice('')}>{item.name}</Link>)}</div>}
                  <div className={classes.dates}><BlogDate label="公開日" value={post.published_at} /><BlogDate label="更新日" value={post.updated_at} /></div>
                  <div className={`${classes.dates} ${classes.reactionInfo}`} role="group" aria-label="記事の反応数" aria-describedby="blog-reaction-counts-note blog-comment-counts-note">
                    <span>いいね {reactionCount(post.like_count)}</span><span>共有操作数 {reactionCount(post.share_operation_count)}</span><span>コメント {reactionCount(post.comment_count)}</span>
                  </div>
                </div>
                <div className={classes.actions}>
                  <Button component={Link} role="link" to={withQuery(`/blog/manage/${post.id}/edit`)} variant="outlined">編集する</Button>
                  {post.status === 'draft' && <Button className={classes.touchAction} component={Link} role="link" to={withQuery(`/blog/manage/${post.id}/preview`)} variant="outlined">下書きをプレビュー</Button>}
                  {state.capabilities.can_moderate && <Button className={classes.touchAction} component={Link} role="link" to={`${withQuery(`/blog/manage/${post.id}/edit`)}#publication-control`} variant="outlined">公開管理</Button>}
                </div>
              </article></li>)}</ul>
              <Typography id="blog-reaction-counts-note" className={classes.reactionInfo} variant="body2">共有操作数は参考情報です。X・Facebook・メールはボタンのクリック数、URLコピーは成功回数を記録しており、実際に共有・送信されたかは分かりません。</Typography>
              <Typography id="blog-comment-counts-note" className={classes.reactionInfo} variant="body2">コメント数は返信を含み、削除済みを除きます。</Typography>
              {state.pagination && <BlogPagination page={state.pagination.page} totalPages={state.pagination.total_pages} onPageChange={changePage} label="ブログ管理のページ送り" />}
              <OwnBlogShare className={classes.indexShare} profile={publicName.profile} loading={publicName.loading} error={publicName.error} onRetry={publicName.reload} />
            </>}
            {editing && <>
              {state.post && <PostStates post={state.post} />}
              {state.post?.is_publication_blocked && <Typography>差し止め中も編集できます。公開設定を保存しても差し止めは解除されません。</Typography>}
              {(actionError || Object.keys(errors).length > 0) && <div role="alert" tabIndex={-1} ref={summaryRef} className={classes.alert}>
                <Typography>{actionError ? blogErrorMessage(actionError, true) : '入力内容を確認してください。'}</Typography>
                {Object.entries(errors).map(([field, message]) => <div key={field}>{field === 'public_name'
                  ? <><Typography>{message}</Typography>{ownPost && <PublicNameSettingsLink />}</>
                  : <a href={`#${fieldIds[field]}`} onClick={(event) => { event.preventDefault(); document.getElementById(fieldIds[field])?.focus(); }}>{labels[field]}：{message}</a>}</div>)}
                {actionError?.fields?.includes('public_name') && <><Typography>記事所有者の共通公開名を設定してください。</Typography>{ownPost && <PublicNameSettingsLink />}</>}
              </div>}
              {success && <Typography role="status">{success}</Typography>}
              <form className={classes.form} noValidate onSubmit={(event) => { event.preventDefault(); save('draft'); }} aria-busy={busy}>
                {textField('title')}
                <BlogAuthorName name={authorName} own={ownPost} loading={ownPost && publicName.loading} error={ownPost && publicName.error} onRetry={publicName.reload} />
                <div className={classes.field}>
                  <label htmlFor={fieldIds.cover_image}>代表画像（任意・1枚）</label>
                  <input id={fieldIds.cover_image} ref={fileInputRef} type="file" accept={`${coverTypes.join(',')},.pdf`}
                    disabled={busy} aria-invalid={Boolean(errors.cover_image)} aria-describedby={`blog-cover-help${errors.cover_image ? ' blog-cover-error' : ''}`}
                    onChange={(event) => {
                      const file = event.target.files?.[0] || null;
                      setCoverFile(file);
                      setSuccess('');
                      setErrors((current) => {
                        const next = { ...current };
                        delete next.cover_image;
                        const message = validateImage(file);
                        return message ? { ...next, cover_image: message } : next;
                      });
                    }} />
                  <Typography id="blog-cover-help" variant="body2">JPEG・PNG・WebP・GIF・PDF、5MiB以下。推奨アスペクト比は横4：縦3（4:3）、サイズ例は1200×900px。画像は全体を表示し、比率が異なる場合は余白が入ります。選択したファイルは保存時に差し替わります。ファイルを選ばず保存すると現在の画像を保持します。</Typography>
                  {errors.cover_image && <Typography id="blog-cover-error" color="error">{errors.cover_image}</Typography>}
                  {pdfSelected && <Typography role="status" variant="body2">PDFは保存時に1ページ目を画像に変換します。</Typography>}
                  {!pdfSelected && (previewUrl || state.post?.cover_image) && <img className={classes.image} src={previewUrl || state.post.cover_image}
                    alt={previewUrl ? '選択した代表画像のプレビュー' : '現在の代表画像'} />}
                </div>
                {textField('body')}
                <BlogTagEditor ids={tagIds} onChange={(ids) => { setTagIds(ids); setTagsChanged(true); setSuccess(''); }}
                  disabled={busy} sendingRef={tagSending} onBusyChange={setTagBusy} />
                <details className={classes.details} open={otherSettingsOpen} onToggle={(event) => setOtherSettingsOpen(event.currentTarget.open)}>
                  <summary>その他の設定（記事URL・概要）</summary>
                  {textField('slug')}{textField('excerpt')}
                </details>
                <div className={classes.saveActions}>
                  <Button type="submit" variant="outlined" disabled={busy || tagBusy}>{state.post?.status === 'published' ? '下書きに戻して保存' : '下書き保存'}</Button>
                  <Button type="button" variant="contained" color="primary" disabled={busy || tagBusy} onClick={() => save('published')}>公開する</Button>
                </div>
                {busy && <Typography role="status">保存しています。</Typography>}
              </form>
              {state.post && state.capabilities.can_moderate && <Paper component="section" id="publication-control" aria-labelledby="publication-control-title" ref={publicationRef} tabIndex={-1} className={classes.card} variant="outlined">
                <Typography component="h2" id="publication-control-title" variant="h6">管理者の公開制御</Typography>
                <Typography>下書き・公開の設定とは別に、公開を差し止めます。解除後は記事の公開設定に従います。</Typography>
                <div className={classes.actions}><Button onClick={moderate} disabled={busy || tagBusy} variant="outlined" color="secondary">{state.post.is_publication_blocked ? '公開差し止めを解除' : '公開を差し止める'}</Button></div>
              </Paper>}
            </>}
          </>}
    {editing ? <div className={classes.editorNavigation}>
      <Button component={Link} role="link" to={withQuery('/blog/manage')} variant="outlined">管理記事一覧へ</Button>
      <Button component={Link} role="link" to="/blog" variant="outlined">公開記事一覧へ</Button>
    </div> : <div className={classes.actions}><Button component={Link} role="link" to="/blog" variant="outlined">公開記事一覧へ</Button></div>}
  </div>;
}

export default function BlogManagePage() {
  const auth = useContext(AuthContext) || {};
  const { postId } = useParams();
  const location = useLocation();
  const account = auth.user?.id || auth.user?.uuid || auth.user?.email || '';
  // A route/account change unmounts all pending loads and mutations before reuse.
  return <BlogLayout manage={true}><ManageContent
    key={`${location.pathname}:${Boolean(auth.isAuth)}:${account}`}
    isAuth={Boolean(auth.isAuth)} postId={postId} creating={location.pathname.replace(/\/+$/, '') === '/blog/manage/new'}
  /></BlogLayout>;
}
